import { Check, Send } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { OutlinedTextField } from '@my2n/react-hook-form-inputs';
import { FormProvider } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { FilledSuccessBar } from '../../../../components/_DesignSystemWrappers/SuccessBar/FilledSuccessBar';
import { PATHS } from '../../../../config/consts';
import { useOryRecoveryApi } from '../../../../ory/hooks/oryApi';
import { IOryRecoveryFlow } from '../../../../ory/types';
import { RouterLink } from '../Link/PrimaryLink';
import messages from './messages';
import { useRecoveryCodeForm } from './useRecoveryCodeForm';

export interface IRecoveryFormProps {
  recoveryFlowData: IOryRecoveryFlow;
  email: string;
  onSuccess: () => void;
  onRecoveryFlowRestart: (newFlowId?: string) => Promise<boolean>;
  onRecoveryProcessRestart: () => void;
}

export function RecoveryCodeForm({
  email,
  onRecoveryFlowRestart,
  onRecoveryProcessRestart,
  onSuccess,
  recoveryFlowData,
}: IRecoveryFormProps) {
  const { formatMessage } = useIntl();

  const { isOryApiFetching, resendRecoveryCode } = useOryRecoveryApi();
  const { formData, formSubmit, hasNewFlowData, resendCode } = useRecoveryCodeForm({
    email,
    onRecoveryFlowRestart,
    onRecoveryProcessRestart,
    onSuccess,
    recoveryFlowData,
    resendRecoveryCode,
  });

  return (
    <FormProvider {...formData}>
      <form onSubmit={formData.handleSubmit(formSubmit)}>
        <Stack spacing={3}>
          <FilledSuccessBar title={formatMessage(messages.ImsResetPasswordAlertTitle, { email })}>
            <Typography>
              <FormattedMessage {...messages.ImsResetPasswordAlertBody} />
            </Typography>
          </FilledSuccessBar>
          <OutlinedTextField
            label={<FormattedMessage {...messages.ImsResetPasswordRecoveryCodeLabel} />}
            name="recoveryCode"
            disabled={hasNewFlowData}
            required
            fullWidth
            autoFocus
            helperText={<FormattedMessage {...messages.ImsResetPasswordRecoveryCodeHelper} />}
          />
        </Stack>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Button
            color="primary"
            disabled={!formData.formState.isValid || isOryApiFetching || hasNewFlowData}
            fullWidth
            size="large"
            startIcon={<Check />}
            type="submit"
            variant="contained"
          >
            <FormattedMessage {...messages.ImsResetPasswordButtonSubmit} />
          </Button>
          <Button
            color="primary"
            disabled={isOryApiFetching}
            fullWidth
            onClick={resendCode}
            size="large"
            startIcon={<Send />}
            variant="outlined"
          >
            <FormattedMessage {...messages.ImsResetPasswordButtonResendCode} />
          </Button>
          <RouterLink to={PATHS.LOGIN}>
            <FormattedMessage {...messages.ImsResetPasswordButtonBackToLogin} />
          </RouterLink>
        </Stack>
      </form>
    </FormProvider>
  );
}
